/**
 * What an employee's own leave adds up to.
 *
 * The balance arrives as `[{ leaveType: <id>, quantity }]` — an id and a
 * number, with no name attached — so every screen that wants to say "Casual: 6"
 * has to join it against the type list itself. Doing that in one place is the
 * difference between the counter band and the request table calling the same
 * leave type by the same name.
 *
 * What is deliberately absent: an entitlement denominator. The design book's
 * cards read "12.5 of 24 days" behind a progress bar, and this product exposes
 * the remaining balance without the annual grant it was drawn from. A bar needs
 * a denominator, and inventing one — last year's opening, or the largest
 * balance ever seen — would draw a proportion that is not true. So the figures
 * are stated as figures until the entitlement is actually available.
 */

import { canCancel } from "../../../../../utils/leaveMonth";

export { canCancel };

const idOf = (value) => {
  if (!value) return null;
  if (typeof value === "string") return value;
  return value._id ? String(value._id) : String(value);
};

/**
 * Balances, named, largest first.
 *
 * A type the employee holds no balance in is dropped rather than shown as
 * zero: an unused leave type is not the same as an exhausted one, and a band
 * padded with zeroes buries the balances that exist.
 */
export const balanceByType = (balances = [], types = []) => {
  const byId = new Map((types || []).map((t) => [idOf(t?._id), t]));
  return (balances || [])
    .map((b) => {
      const type = byId.get(idOf(b?.leaveType));
      const quantity = Number(b?.quantity) || 0;
      if (!type || quantity <= 0) return null;
      return {
        id: idOf(type._id),
        name: type.name || type.leaveCode || "Leave",
        code: type.leaveCode || null,
        isPaid: type.isPaid !== false,
        quantity,
      };
    })
    .filter(Boolean)
    .sort((a, b) => b.quantity - a.quantity);
};

/** Everything left, across every type. */
export const totalBalance = (balances = []) =>
  (balances || []).reduce((sum, b) => sum + (Number(b?.quantity) || 0), 0);

/** "6 Casual · 4.5 Sick" — the breakdown that makes a total checkable. */
export const balanceBreakdown = (named = []) =>
  named.map((b) => `${b.quantity} ${b.name}`).join(" · ");

const STATUS = {
  PENDING: "Pending",
  APPROVED: "Approved",
  REJECTED: "Rejected",
  CANCELLED: "Cancelled",
};

export const pendingRequests = (requests = []) =>
  (requests || []).filter((r) => r?.status === STATUS.PENDING);

/**
 * The line under the heading: what, if anything, is waiting on somebody else.
 *
 * Names the approver when there is exactly one request outstanding, because
 * "waiting on Alex Reyes" is actionable in a way "1 pending" is not. With
 * several it stays a count — listing three names in a subheading is a list, not
 * a sentence.
 */
export const waitingLine = (requests = [], approver = null) => {
  const pending = pendingRequests(requests);
  if (!pending.length) return "Nothing waiting on approval";
  const name = approver?.fname
    ? `${approver.fname} ${approver.lname || ""}`.trim()
    : null;
  if (pending.length === 1 && name) return `1 request waiting on ${name}`;
  return `${pending.length} request${pending.length === 1 ? "" : "s"} waiting on approval`;
};

/**
 * How many days a request covers.
 *
 * Counts the day entries the request carries rather than differencing the
 * dates: a range spanning a weekend or a holiday is not the number of days
 * debited, and the entries are what the balance was actually drawn against.
 */
export const requestDays = (request) => (request?.days || []).length || null;

/**
 * Whether the employee can take back their own request.
 *
 * Wider than `canCancel`, which the admin screen shares: a manager declines a
 * pending request rather than cancelling it, but the person who asked can
 * simply withdraw it — nothing has left their balance yet. Approved leave
 * follows `canCancel`, so the two screens never disagree about when leave has
 * started.
 */
export const canWithdraw = (request) =>
  request?.status === STATUS.PENDING || canCancel(request);

/**
 * Why a request cannot be taken back, when it cannot.
 *
 * `canWithdraw` answers yes or no; a screen also has to say why not, because a
 * missing button with no explanation reads as something broken. Leave already
 * under way is a record of what happened rather than a plan.
 */
export const cancelBlockReason = (request) => {
  if (!request) return null;
  if (canWithdraw(request)) return null;
  if (request.status === STATUS.APPROVED) {
    return "This leave has already started, so it stays on your record.";
  }
  return null;
};
